import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTranslation } from '../i18n';
const content = {
  en: {
    title: 'Terms & Conditions',
    description: 'Terms and conditions for using Dentico Clinic website and services.',
    lastUpdated: 'Last updated: 18 June 2026',
    sections: [{
      title: '1. Introduction',
      content: 'Welcome to Dentico Clinic. These Terms & Conditions govern your use of our website and the dental services provided at our clinic. By accessing our website or booking an appointment, you agree to be bound by these terms.'
    }, {
      title: '2. Website Use',
      content: 'Our website is provided for informational purposes and to facilitate appointment bookings. You agree to use the site lawfully and not to use it for any malicious activities, including transmitting viruses or attempting unauthorized access.'
    }, {
      title: '3. Appointments and Bookings',
      content: 'All appointments are subject to availability. We reserve the right to modify or cancel appointments when necessary, though we will always strive to provide adequate notice and reschedule at your earliest convenience.'
    }, {
      title: '4. Deposits and Payments',
      content: 'A deposit may be required to secure certain treatments or lengthy appointments. Full payment for services is generally due on the day of treatment unless a prior finance agreement has been arranged.'
    }, {
      title: '5. Cancellations and Missed Appointments',
      content: 'We require at least 48 hours notice for appointment cancellations or rescheduling. Failure to provide sufficient notice or missing an appointment may result in a cancellation fee or the loss of your deposit.'
    }, {
      title: '6. Treatment Information and Estimates',
      content: 'Any treatment plans and cost estimates provided are valid for a specified period (usually 30 days). Clinical situations can change, and we reserve the right to amend treatment plans and costs if necessary, which will always be discussed with you first.'
    }, {
      title: '7. Website Content Disclaimer',
      content: 'The medical and dental information on this website is for general guidance only and does not replace professional medical advice. Always consult with a qualified dentist for diagnosis and treatment planning.'
    }, {
      title: '8. Intellectual Property',
      content: 'All content on this website, including text, images, logos, and design, is the property of Dentico Clinic and is protected by copyright laws. Unauthorized use, reproduction, or distribution is strictly prohibited.'
    }, {
      title: '9. External Links',
      content: 'Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of use of these external sites.'
    }],
    privacy: {
      title: '10. Privacy and Personal Data',
      text: 'We take your privacy seriously. The collection and processing of your personal and medical data are governed by our ',
      linkText: 'Privacy Policy',
      textAfter: '.'
    },
    contact: {
      title: '11. Contact Information',
      text: 'If you have any questions about these Terms & Conditions, please contact us:',
      name: 'Dentico Clinic',
      address: '360–364 City Road, London EC1V 2PY',
      email: 'info@denticoclinic.co.uk',
      phone: '07345 197820'
    }
  },
  ua: {
    title: 'Умови та положення',
    description: 'Умови та положення використання вебсайту та послуг Dentico Clinic.',
    lastUpdated: 'Останнє оновлення: 18 червня 2026 р.',
    sections: [{
      title: '1. Вступ',
      content: 'Ласкаво просимо до Dentico Clinic. Ці Умови та положення регулюють використання нашого вебсайту та стоматологічних послуг, що надаються в нашій клініці. Отримуючи доступ до нашого сайту або записуючись на прийом, ви погоджуєтеся з цими умовами.'
    }, {
      title: '2. Використання вебсайту',
      content: 'Наш вебсайт надається для інформаційних цілей та для полегшення запису на прийом. Ви погоджуєтеся використовувати сайт законно і не використовувати його для будь-якої зловмисної діяльності.'
    }, {
      title: '3. Запис на прийом',
      content: 'Усі записи залежать від наявності вільного часу. Ми залишаємо за собою право змінювати або скасовувати прийоми в разі необхідності, хоча ми завжди намагатимемося попередити вас заздалегідь.'
    }, {
      title: '4. Депозити та платежі',
      content: 'Для бронювання певних процедур може знадобитися депозит. Повна оплата послуг зазвичай здійснюється в день лікування, якщо не було укладено попередню угоду про розстрочку.'
    }, {
      title: '5. Скасування та пропущені візити',
      content: 'Ми вимагаємо повідомляти про скасування або перенесення візиту щонайменше за 48 годин. Недотримання цього правила може призвести до стягнення плати за скасування або втрати депозиту.'
    }, {
      title: '6. Інформація про лікування та оцінка вартості',
      content: 'Надані плани лікування та оцінка вартості дійсні протягом певного періоду (зазвичай 30 днів). Клінічні ситуації можуть змінюватися, і ми залишаємо за собою право коригувати плани лікування та вартість.'
    }, {
      title: '7. Відмова від відповідальності щодо контенту',
      content: 'Медична та стоматологічна інформація на цьому сайті призначена лише для загального ознайомлення і не замінює професійної медичної консультації.'
    }, {
      title: '8. Інтелектуальна власність',
      content: 'Увесь контент на цьому вебсайті є власністю Dentico Clinic і захищений законами про авторське право. Несанкціоноване використання суворо заборонено.'
    }, {
      title: '9. Зовнішні посилання',
      content: 'Наш вебсайт може містити посилання на сторонні вебсайти. Ми не несемо відповідальності за контент або політику конфіденційності цих зовнішніх сайтів.'
    }],
    privacy: {
      title: '10. Конфіденційність та персональні дані',
      text: 'Ми серйозно ставимося до вашої конфіденційності. Збір та обробка ваших даних регулюються нашою ',
      linkText: 'Політикою конфіденційності',
      textAfter: '.'
    },
    contact: {
      title: '11. Контактна інформація',
      text: "Якщо у вас виникли запитання щодо цих Умов, будь ласка, зв'яжіться з нами:",
      name: 'Dentico Clinic',
      address: '360–364 City Road, London EC1V 2PY',
      email: 'info@denticoclinic.co.uk',
      phone: '07345 197820'
    }
  },
  ru: {
    title: 'Условия и положения',
    description: 'Условия и положения использования веб-сайта и услуг Dentico Clinic.',
    lastUpdated: 'Последнее обновление: 18 июня 2026 г.',
    sections: [{
      title: '1. Введение',
      content: 'Добро пожаловать в Dentico Clinic. Настоящие Условия регулируют использование нашего веб-сайта и стоматологических услуг. Получая доступ к сайту или записываясь на прием, вы соглашаетесь с этими условиями.'
    }, {
      title: '2. Использование веб-сайта',
      content: 'Наш веб-сайт предоставляется в информационных целях и для записи на прием. Вы обязуетесь использовать сайт законно и не использовать его для вредоносной деятельности.'
    }, {
      title: '3. Запись на прием',
      content: 'Все записи зависят от наличия свободного времени. Мы оставляем за собой право изменять или отменять приемы при необходимости, предварительно уведомив вас.'
    }, {
      title: '4. Депозиты и платежи',
      content: 'Для бронирования определенных процедур может потребоваться депозит. Полная оплата услуг обычно производится в день лечения, если не оформлена рассрочка.'
    }, {
      title: '5. Отмена и пропущенные визиты',
      content: 'Мы требуем уведомлять об отмене или переносе визита не менее чем за 48 часов. Несоблюдение этого правила может привести к штрафу или потере депозита.'
    }, {
      title: '6. Информация о лечении и стоимость',
      content: 'Предоставленные планы лечения и оценки стоимости действительны в течение определенного периода (обычно 30 дней). Клинические ситуации могут меняться, и мы можем корректировать планы.'
    }, {
      title: '7. Отказ от ответственности за контент',
      content: 'Информация на этом сайте предназначена только для общего ознакомления и не заменяет профессиональной медицинской консультации.'
    }, {
      title: '8. Интеллектуальная собственность',
      content: 'Весь контент на этом веб-сайте является собственностью Dentico Clinic и защищен законами об авторском праве.'
    }, {
      title: '9. Внешние ссылки',
      content: 'Наш веб-сайт может содержать ссылки на сторонние сайты. Мы не несем ответственности за их контент или политику конфиденциальности.'
    }],
    privacy: {
      title: '10. Конфиденциальность и персональные данные',
      text: 'Мы серьезно относимся к вашей конфиденциальности. Сбор и обработка ваших данных регулируются нашей ',
      linkText: 'Политикой конфиденциальности',
      textAfter: '.'
    },
    contact: {
      title: '11. Контактная информация',
      text: 'Если у вас есть вопросы по этим Условиям, свяжитесь с нами:',
      name: 'Dentico Clinic',
      address: '360–364 City Road, London EC1V 2PY',
      email: 'info@denticoclinic.co.uk',
      phone: '07345 197820'
    }
  },
  ro: {
    title: 'Termeni și condiții',
    description: 'Termeni și condiții pentru utilizarea site-ului și serviciilor Dentico Clinic.',
    lastUpdated: 'Ultima actualizare: 18 Iunie 2026',
    sections: [{
      title: '1. Introducere',
      content: 'Bun venit la Dentico Clinic. Acești Termeni și Condiții guvernează utilizarea site-ului nostru și a serviciilor stomatologice. Prin accesarea site-ului sau programarea unei vizite, sunteți de acord cu acești termeni.'
    }, {
      title: '2. Utilizarea site-ului',
      content: 'Site-ul nostru este furnizat în scopuri informative și pentru a facilita programările. Sunteți de acord să utilizați site-ul în mod legal și să nu îl folosiți pentru activități rău intenționate.'
    }, {
      title: '3. Programări',
      content: 'Toate programările sunt supuse disponibilității. Ne rezervăm dreptul de a modifica sau anula programările atunci când este necesar, deși vom încerca întotdeauna să vă anunțăm în prealabil.'
    }, {
      title: '4. Depozite și plăți',
      content: 'Poate fi necesar un depozit pentru a asigura anumite tratamente. Plata integrală a serviciilor se face în general în ziua tratamentului, cu excepția cazului în care s-a convenit un plan de finanțare.'
    }, {
      title: '5. Anulări și programări ratate',
      content: 'Solicităm un preaviz de cel puțin 48 de ore pentru anularea sau reprogramarea vizitelor. Nerespectarea acestui termen poate duce la o taxă de anulare sau pierderea depozitului.'
    }, {
      title: '6. Informații despre tratament și estimări',
      content: 'Planurile de tratament și estimările de costuri sunt valabile pentru o perioadă specificată (de obicei 30 de zile). Situațiile clinice se pot schimba, iar noi ne rezervăm dreptul de a modifica planurile și costurile.'
    }, {
      title: '7. Declinarea responsabilității pentru conținut',
      content: 'Informațiile medicale de pe acest site sunt doar pentru orientare generală și nu înlocuiesc sfatul medical profesional.'
    }, {
      title: '8. Proprietate intelectuală',
      content: 'Întregul conținut de pe acest site este proprietatea Dentico Clinic și este protejat de legile drepturilor de autor. Utilizarea neautorizată este strict interzisă.'
    }, {
      title: '9. Linkuri externe',
      content: 'Site-ul nostru poate conține linkuri către site-uri terțe. Nu suntem responsabili pentru conținutul sau practicile de confidențialitate ale acestor site-uri externe.'
    }],
    privacy: {
      title: '10. Confidențialitate și date personale',
      text: 'Luăm în serios confidențialitatea dvs. Colectarea și prelucrarea datelor dvs. sunt guvernate de ',
      linkText: 'Politica noastră de confidențialitate',
      textAfter: '.'
    },
    contact: {
      title: '11. Informații de contact',
      text: 'Dacă aveți întrebări despre acești Termeni și Condiții, vă rugăm să ne contactați:',
      name: 'Dentico Clinic',
      address: '360–364 City Road, London EC1V 2PY',
      email: 'info@denticoclinic.co.uk',
      phone: '07345 197820'
    }
  }
};
export function TermsAndConditionsPage() {
  const {
    lang
  } = useTranslation();
  const t = content[lang as keyof typeof content] || content.en;
  return <Layout title={t.title} description={t.description} data-id="element-4004">
      {/* Header Section */}
      <section className="bg-forest-900 text-ivory py-16 md:py-24" data-id="element-4005">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-4006">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium mb-4" data-id="element-4007">
            {t.title}
          </h1>
          <p className="text-ivory/70 text-sm md:text-base" data-id="element-4008">{t.lastUpdated}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-[#F8F5F0] py-16 md:py-24" data-id="element-4009">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4010">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 md:p-10 lg:p-12 space-y-10 text-stone-600 leading-relaxed" data-id="element-4011">
            {t.sections.map((section, index) => <div key={index} className="space-y-3" data-id="element-4012">
                <h2 className="text-xl md:text-2xl font-medium text-forest-900" data-id="element-4013">
                  {section.title}
                </h2>
                <p data-id="element-4014">{section.content}</p>
              </div>)}

            {/* Privacy Policy Reference */}
            <div className="space-y-3" data-id="element-4015">
              <h2 className="text-xl md:text-2xl font-medium text-forest-900" data-id="element-4016">
                {t.privacy.title}
              </h2>
              <p data-id="element-4017">
                {t.privacy.text}
                <Link to={`/${lang}/privacy-policy`} className="text-forest-600 underline decoration-forest-600/30 underline-offset-4 hover:decoration-forest-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-600 rounded-sm transition-all" data-id="element-4018">
                  {t.privacy.linkText}
                </Link>
                {t.privacy.textAfter}
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 pt-6 border-t border-stone-200" data-id="element-4019">
              <h2 className="text-xl md:text-2xl font-medium text-forest-900" data-id="element-4020">
                {t.contact.title}
              </h2>
              <p data-id="element-4021">{t.contact.text}</p>
              <div className="bg-[#F8F5F0] p-6 rounded-xl space-y-2 text-forest-900" data-id="element-4022">
                <p className="font-medium" data-id="element-4023">{t.contact.name}</p>
                <p data-id="element-4024">{t.contact.address}</p>
                <p data-id="element-4025">
                  Email:{' '}
                  <a href={`mailto:${t.contact.email}`} className="text-forest-600 hover:text-forest-700 underline decoration-forest-600/30 underline-offset-4 hover:decoration-forest-600 transition-all" data-id="element-4026">
                    {t.contact.email}
                  </a>
                </p>
                <p data-id="element-4027">
                  Phone:{' '}
                  <a href={`tel:${t.contact.phone.replace(/\s/g, '')}`} className="text-forest-600 hover:text-forest-700 underline decoration-forest-600/30 underline-offset-4 hover:decoration-forest-600 transition-all" data-id="element-4028">
                    {t.contact.phone}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}